enum ErrorCode {
  AuthorizedQuantityExceeded = '04',
  RequiredApplicationDataMissing = '15',
  InputErrors = '33',
  OutofNetwork = '35',
  AuthorizationAccessRestrictions = '41',
  UnableToRespond = '42',
  InvalidProviderIdentification = '43',
  InvalidOrMissingProviderName = '44',
  InvalidOrMissingProviderSpecialty = '45',
  InvalidOrMissingProviderPhoneNumber = '46',
  InvalidOrMissingProviderState = '47',
  InvalidOrMissingReferringProviderIdentificationNumber = '48',
  ProviderIsNotPrimaryCarePhysician = '49',
  ProviderIneligibleForInquiries = '50',
  ProviderNotonFile = '51',
  ServiceDatesNotWithinProviderPlanEnrollment = '52',
  InquiredBenefitInconsistentWithProviderType = '53',
  InappropriateProductOrServiceIDQualifier = '54',
  InappropriateProductOrServiceID = '55',
  InappropriateDate = '56',
  InvalidOrMissingDateOfService = '57',
  InvalidOrMissingDateOfBirth = '58',
  DateOfBirthFollowsDatesOfService = '60',
  DateOfDeathPrecedesDatesOfService = '61',
  DateOfServiceNotWithinAllowableInquiryPeriod = '62',
  DateOfServiceInFuture = '63',
  InvalidOrMissingPatientID = '64',
  InvalidOrMissingPatientName = '65',
  InvalidOrMissingPatientGenderCode = '66',
  PatientNotFound = '67',
  DuplicatePatientIDNumber = '68',
  InconsistentWithPatientAge = '69',
  InconsistentWithPatientGender = '70',
  PatientBirthDateDoesNotMatch = '71',
  InvalidOrMissingSubscriberOrInsuredID = '72',
  InvalidOrMissingSubscriberOrInsuredName = '73',
  InvalidOrMissingSubscriberOrInsuredGenderCode = '74',
  SubscriberOrInsuredNotFound = '75',
  DuplicateSubscriberOrInsuredIDNumber = '76',
  SubscriberFoundPatientNotFound = '77',
  SubscriberOrInsuredNotInGroupOrPlanIdentified = '78',
  InvalidParticipantIdentification = '79',
  NoResponseReceivedTransactionTerminated = '80',
  InvalidOrMissingProviderAddress = '97',
  ExperimentalServiceOrProcedure = '98',
  AuthorizationNumberNotFound = 'AA',
  RequiresPrimaryCarePhysicianAuthorization = 'AE',
  InvalidOrMissingdiagnosisCode = 'AF',
  InvalidOrMissingprocedureCode = 'AG',
  AdditionalPatientCoordinationInformationRequired = 'AO',
  CertificationInformationDoesNotMatchPatient = 'CI',
  RequiresMedicalReview = 'E8',
  InvalidAuthorizationNumberFormat = 'IA',
  MissingAuthorizationNumber = 'MA',
  PayerNameOrIdentifierMissing = 'T4',
}

interface CoverageLinks {
  self: {
    href: string;
  };
}

interface BaseCoveragePerson {
  firstName: string;
  lastName: string;
  gender?: string;
  genderCode?: string;
  birthDate?: string;
}

interface BaseCoverageSubscriber extends BaseCoveragePerson {
  memberId: string;
}

interface BaseCoveragePatient extends BaseCoveragePerson {
  subscriberRelationship: AvailitySubscriberRelationship;
  subscriberRelationshipCode: string;
}

interface BasePlan {
  status: string;
  statusCode: PlanStatusCode;
}

interface RequestedServiceType {
  code: string;
  value: string;
}

interface RequestingProvider {
  npi: string;
  taxId: string;
}

interface ErrorMessage {
  errorMessage: string;
}

interface ValidationMessages extends ErrorMessage {
  code: ErrorCode;
}

interface Payer {
  name: string;
  payerId: string;
  responseName?: string;
  responsePayerId?: string;
  contactInformation?: object[];
}

interface BaseCoverage {
  links: CoverageLinks;
  id: string;
  customerId: string;
  controlNumber: string;
  status: string;
  statusCode: RequestStatusCode;
  createdDate: string;
  updatedDate: string;
  expirationDate: string;
  asOfDate: string;
  requestedServiceType: RequestedServiceType[];
  validationMessages: ValidationMessages[];
  payer: Payer;
  requestingProvider: RequestingProvider;
}

enum RequestStatusCode {
  RequestError = '19',
  CommunicationErrorRetrying = 'R1',
  CommunicationErrorNoResponse = '7',
  CommunicationErrorInvalid = '13',
  CommunicationErrorNoHealthPlanResponse = '14',
  CommunicationErrorHealthPlanMaintenance = '15',
  Complete = '4',
  CompleteInvalidResponse = '3',
  InProgress = '0',
}

enum AvailitySubscriberRelationship {
  Self = 'Self',
  Spouse = 'Spouse',
  Child = 'Child',
  Employee = 'Employee',
  Unknown = 'Unknown',
  OrganDonor = 'Organ Donor',
  CadaverDonor = 'Cadaver Donor',
  LifePartner = 'Life Partner',
  Other = 'Other Relationship',
  OtherAdult = 'Other Adult',
}

enum PlanStatusCode {
  CoverageUnknown = '0',
  ActiveCoverage = '1',
  ActiveFullRiskCapitation = '2',
  ActiveServicesCapitated = '3',
  ActiveServiceCapitatedToPrimaryCarePhysician = '4',
  ActivePendingInvestigation = '5',
  Inactive = '6',
  InactivePendingEligibilityUpdate = '7',
  InactivePendingInvestigation = '8',
}

interface DetailedCoverageSubscriber extends BaseCoverageSubscriber {
  address: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    stateCode: string;
    zipCode: string;
  };
}
interface DetailedCoveragePatient extends BaseCoveragePatient {
  address: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    stateCode: string;
    zipCode: string;
  };
  updateYourRecords: boolean;
}

export interface DetailedPlan extends BasePlan {
  groupNumber: string;
  groupName: string;
  serviceDate: string;
  coverageStartDate: string;
  coverageEndDate?: string;
  benefits: object[];
  payerNotes?: object[];
}

export interface DetailedCoverage extends BaseCoverage {
  patient: DetailedCoveragePatient;
  subscriber: DetailedCoverageSubscriber;
  plans: DetailedPlan[];
  supplementalInformation: object;
}

interface Coverage extends BaseCoverage {
  subscriber: BaseCoverageSubscriber;
  patient: BaseCoveragePatient;
  plans: BasePlan[];
}

interface MissingInformationError extends ErrorMessage {
  field: string;
}
export interface CoveragesSearchResult {
  totalCount: number;
  count: number;
  offset: number;
  limit: number;
  links: CoverageLinks;
  coverages: Coverage[];
  statusCode?: number;
}

export interface CoverageSearchError {
  userMessage: string;
  developerMessage: string;
  documentation?: string;
  reasonCode?: number;
  statusCode: number;
  errors: MissingInformationError[];
}
